import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api, { talentAPI, generalAPI, matchingAPI } from '../../utils/api';
import GigCodeOfConductModal from '../../components/GigCodeOfConductModal';
import LinkifiedText from '../../components/LinkifiedText';
import ClientReputationBadge from '../../components/ClientReputationBadge';  // Iter-225r
import { AIPickBadge } from '../../components/AIBadges';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ArrowLeft, MapPin, Calendar, DollarSign, Users, Clock, Zap, TrendingUp, Sparkles, Building2, AlertTriangle, CheckCircle, Star, Flame, Gift, Shield, Award, Lock, BookOpen } from 'lucide-react';
import { formatTime12h, formatTimeRange, formatDateShort, formatDateFull, formatDateRange, formatDate } from '../../utils/timeFormat';
import { isFlatPay, formatPay } from '../../utils/payFormat';
import { toast } from 'sonner';
import OneTapApplyButton from '../../components/OneTapApplyButton';

// Iter-133c — backend masks operational fields for non-booked viewers
// with this exact placeholder string. We render it as a styled "locked"
// banner so applicants understand the data exists but is gated.
const LOCKED_NOTES_PLACEHOLDER = "Detailed instructions will be available after booking confirmation";
const isLocked = (v) => typeof v === 'string' && v === LOCKED_NOTES_PLACEHOLDER;
const LockedBanner = ({ label }) => (
  <div
    className="p-3 bg-gray-50 border border-dashed border-gray-300 rounded-lg flex items-start gap-2"
    data-testid={`locked-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    <Lock className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
    <div className="text-sm text-gray-600">
      <span className="font-medium text-gray-700">{label}:</span> {LOCKED_NOTES_PLACEHOLDER}.
    </div>
  </div>
);

// Smart Apply eligibility chip — rendered on each shift card so talent knows
// upfront whether they can apply. Verdict map comes from
// POST /talent/eligibility/bulk (see bulkEligibility in utils/api.js).
const EligibilityChip = ({ verdict }) => {
  if (!verdict || verdict.status === 'unknown') {
    return (
      <Badge
        className="bg-gray-100 text-gray-600 border-gray-200 text-[10px]"
        data-testid="eligibility-chip-unknown"
      >
        <Clock className="w-3 h-3 mr-0.5" /> Checking…
      </Badge>
    );
  }
  if (verdict.status === 'eligible') {
    // Iter-313f — GGF (and future post-apply certs) — talent CAN apply,
    // but we surface a friendly amber pill so they know they'll need
    // to complete the cert to be booked.
    if (verdict.pending_certification) {
      return (
        <Badge
          className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]"
          data-testid={`eligibility-chip-cert-pending-${verdict.pending_certification}`}
          title={verdict.pending_certification_message || 'Certification required after applying'}
        >
          <CheckCircle className="w-3 h-3 mr-0.5" /> Apply · cert required
        </Badge>
      );
    }
    return (
      <Badge
        className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]"
        data-testid="eligibility-chip-eligible"
      >
        <CheckCircle className="w-3 h-3 mr-0.5" /> Eligible
      </Badge>
    );
  }
  if (verdict.status === 'already_applied') {
    return (
      <Badge
        className="bg-cyan-100 text-cyan-800 border-cyan-200 text-[10px]"
        data-testid="eligibility-chip-applied"
      >
        <CheckCircle className="w-3 h-3 mr-0.5" /> Applied
      </Badge>
    );
  }
  // not_eligible
  return (
    <Badge
      className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]"
      data-testid="eligibility-chip-not-eligible"
    >
      <Lock className="w-3 h-3 mr-0.5" />
      {verdict.missing_count > 0 ? `${verdict.missing_count} cert${verdict.missing_count > 1 ? 's' : ''} needed` : 'Not eligible'}
    </Badge>
  );
};


// Bonus Badge Component - shows when gig has active incentives
const BonusBadge = ({ shift }) => {
  const [incentiveData, setIncentiveData] = useState(null);
  
  useEffect(() => {
    if (shift?.has_active_incentive || shift?.incentive_display_text) {
      // Use pre-computed display text if available
      if (shift.incentive_display_text) {
        setIncentiveData({ display_text: shift.incentive_display_text });
      } else {
        // Fetch incentive details
        api.get(`/incentives/gig/${shift.id}`)
          .then(res => setIncentiveData(res.data))
          .catch(() => {});
      }
    }
  }, [shift?.id, shift?.has_active_incentive]);
  
  if (!incentiveData && !shift?.has_active_incentive) return null;
  
  const displayText = incentiveData?.display_text || shift?.incentive_display_text;
  const incentives = incentiveData?.incentives || [];
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold shadow-md animate-pulse cursor-pointer" data-testid="bonus-badge">
            <Flame className="w-3.5 h-3.5" />
            {displayText || '+BONUS'}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs p-3 bg-gray-900 text-white">
          <div className="space-y-2">
            <div className="font-semibold flex items-center gap-2 text-orange-400">
              <Gift className="w-4 h-4" />
              Incentive Bonus
            </div>
            {incentives.length > 0 ? (
              <div className="text-xs space-y-1">
                {incentives.map((inc, i) => (
                  <div key={i} className="flex justify-between">
                    <span className="text-gray-300">{inc.rule_name}</span>
                    <span className="font-medium text-green-400">+${inc.bonus_amount}</span>
                  </div>
                ))}
                <div className="border-t border-gray-700 pt-1 mt-1 flex justify-between font-semibold">
                  <span>Total Bonus</span>
                  <span className="text-green-400">+${incentiveData?.total_bonus || 0}</span>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-300">Extra pay available for this gig!</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Role Match Badge - shows when gig matches talent's profile roles
const RoleMatchBadge = ({ matchBreakdown }) => {
  // Check if the role match is from profile gig roles (95% score) or primary role (100% score)
  const roleInfo = matchBreakdown?.role;
  const isProfileMatch = roleInfo?.reason === 'Profile gig role match' || roleInfo?.reason === 'Primary role match';
  
  if (!isProfileMatch || roleInfo?.score < 80) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 text-xs font-medium">
            <Star className="w-3 h-3 fill-emerald-500" />
            Matches Your Roles
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs p-2">
          <p className="text-xs">This gig matches a role you've selected in your profile skills</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Match Score Badge Component for Talent
const GigMatchBadge = ({ score, breakdown }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-700 border-green-300';
    if (score >= 60) return 'bg-cyan-100 text-cyan-700 border-cyan-300';
    if (score >= 40) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${getScoreColor(score)}`}>
            <Zap className="w-3 h-3" />
            {Math.round(score)}% Match
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs p-3">
          <div className="space-y-2">
            <div className="font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Match Score: {Math.round(score)}%
            </div>
            {breakdown && (
              <div className="text-xs space-y-1">
                {breakdown.role?.score >= 80 && <div>✓ Matches your primary role</div>}
                {breakdown.distance?.score >= 80 && <div>✓ Local to your area</div>}
                {breakdown.pay_rate?.score >= 70 && <div>✓ Above your min rate</div>}
                {breakdown.availability?.score >= 80 && <div>✓ Matches your availability</div>}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const TalentShifts = () => {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  // shift_id → { status: 'eligible' | 'already_applied' | 'not_eligible' | 'unknown', missing_count, reason }
  const [eligibility, setEligibility] = useState({});
  const [recommendedGigs, setRecommendedGigs] = useState([]);
  const [agencyJobs, setAgencyJobs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filters, setFilters] = useState({
    city: '',
    state: '',
    role_id: '',
  });
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedAgencyJob, setSelectedAgencyJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRecommended, setLoadingRecommended] = useState(true);
  const [loadingAgency, setLoadingAgency] = useState(true);
  const [applying, setApplying] = useState(false);
  // 2026-08-31 — full assignment schedule for the gig modal. Multi-day
  // gigs must list EVERY shift with date/time/hours; totals derive from
  // these rows so edits recalculate automatically.
  const [gigSchedule, setGigSchedule] = useState(null);
  const [termsGate, setTermsGate] = useState(null); // { shiftId, terms } — V424 payout ack
  const [termsChecked, setTermsChecked] = useState(false);
  // 2026-09 — Legacy apply Code of Conduct gate.
  const [cocVersion, setCocVersion] = useState(null);
  const [cocChecked, setCocChecked] = useState(false);
  const [showCocView, setShowCocView] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/code-of-conduct/current');
        if (!cancelled) setCocVersion(r.data);
      } catch (e) { /* no active policy */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [activeTab, setActiveTab] = useState('recommended');
  const [coverNote, setCoverNote] = useState('');
  const [showDirectHireConfirm, setShowDirectHireConfirm] = useState(false);
  const [agreementStatus, setAgreementStatus] = useState(null);
  
  // Prerequisites state
  const [prerequisiteCheck, setPrerequisiteCheck] = useState(null);
  const [checkingPrereqs, setCheckingPrereqs] = useState(false);

  // Iter-246 — Guardian Garage Floors certification status for the talent.
  // Drives the "Take Assessment" CTA on shifts that require GGF cert.
  const [ggfStatus, setGgfStatus] = useState(null);

  useEffect(() => {
    fetchRoles();
    fetchShifts();
    fetchRecommendedGigs();
    fetchAgencyJobs();
    checkAgreementStatus();
    fetchGgfStatus();
  }, []);

  const fetchGgfStatus = async () => {
    try {
      const r = await api.get('/ggf/status');
      setGgfStatus(r.data);
    } catch (_e) {
      setGgfStatus({ ggf_certified: false });
    }
  };

  const checkAgreementStatus = async () => {
    try {
      const response = await api.get('/agreement/status');
      setAgreementStatus(response.data);
    } catch (error) {
      console.log('Agreement status check failed:', error);
    }
  };
  
  // Check prerequisites when selecting a shift with certification requirements
  const checkPrerequisites = async (shiftId) => {
    setCheckingPrereqs(true);
    try {
      const response = await api.get(`/training/check-prerequisites/${shiftId}`);
      setPrerequisiteCheck(response.data);
    } catch (error) {
      console.log('Prerequisite check failed:', error);
      setPrerequisiteCheck(null);
    } finally {
      setCheckingPrereqs(false);
    }
  };
  
  // Enhanced shift selection with prerequisite check
  const handleSelectShift = async (shift) => {
    setSelectedShift(shift);
    setPrerequisiteCheck(null);
    
    if (shift.certification_required) {
      await checkPrerequisites(shift.id);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await generalAPI.getRoles();
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  // Iter-213+ P0 — Compliance gate: if the talent's IDV isn't approved,
  // /talent/gigs/available returns a structured 403 telling the FE to
  // render an "Upload your driver's license to unlock gigs" CTA panel
  // instead of an empty feed.
  const [complianceGate, setComplianceGate] = useState(null);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const response = await talentAPI.getAvailableShifts(filters);
      setShifts(response.data);
      setComplianceGate(null);
      // Fire Smart-Apply eligibility probe in parallel so chips render the
      // moment each card mounts — one coarse backend call, no N+1.
      const ids = (response.data || []).map((s) => s.id).filter(Boolean);
      if (ids.length) {
        talentAPI
          .bulkEligibility(ids)
          .then((r) => setEligibility((prev) => ({ ...prev, ...(r.data?.eligibility || {}) })))
          .catch(() => {});
      }
    } catch (error) {
      // Compliance gate 403 — server returns { code, reason, detail, action }
      const status = error?.response?.status;
      const data = error?.response?.data?.detail;
      if (status === 403 && data && typeof data === 'object' && data.code === 'compliance_gate') {
        setComplianceGate(data);
        setShifts([]);
      } else {
        toast.error('Failed to load shifts');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendedGigs = async () => {
    setLoadingRecommended(true);
    try {
      const response = await matchingAPI.getRecommendedGigs({ limit: 20, min_score: 40 });
      setRecommendedGigs(response.data?.recommendations || []);
    } catch (error) {
      console.log('Recommended gigs not available:', error);
      setRecommendedGigs([]);
    } finally {
      setLoadingRecommended(false);
    }
  };

  const fetchAgencyJobs = async () => {
    setLoadingAgency(true);
    try {
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.role_id) params.append('role_id', filters.role_id);
      
      const response = await api.get(`/agency/public/jobs?${params.toString()}`);
      setAgencyJobs(response.data || []);
    } catch (error) {
      console.log('Agency jobs not available:', error);
      setAgencyJobs([]);
    } finally {
      setLoadingAgency(false);
    }
  };

  const handleApplyToAgencyJob = async () => {
    if (!selectedAgencyJob) return;
    
    // Check if agreement is accepted
    if (agreementStatus && !agreementStatus.has_accepted) {
      toast.error('You must accept the Independent Contractor Agreement before applying to gigs');
      navigate('/talent/agreement');
      return;
    }
    
    setApplying(true);
    try {
      await api.post(`/agency/public/jobs/${selectedAgencyJob.id}/apply`, {
        job_id: selectedAgencyJob.id,
        cover_note: coverNote,
        ...(cocChecked && cocVersion ? { coc_agreed: true, coc_version_id: cocVersion.id } : {}),
      });
      toast.success('Application submitted!');
      setShowDirectHireConfirm(true);
      setSelectedAgencyJob(null);
      setCoverNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => {
    setGigSchedule(null);
    const id = selectedShift?.id;
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/talent/gigs/${id}/schedule`);
        if (!cancelled) setGigSchedule(res.data);
      } catch {
        // non-fatal — modal falls back to single-shift math
      }
    })();
    return () => { cancelled = true; };
  }, [selectedShift?.id]);

  const handleApply = async (ack = false) => {
    if (!selectedShift) return;

    // Check if agreement is accepted
    if (agreementStatus && !agreementStatus.has_accepted) {
      toast.error('You must accept the Independent Contractor Agreement before applying to gigs');
      navigate('/talent/agreement');
      return;
    }

    // Iter-315j — Guardian Garage Floors pre-flight. If the shift
    // requires GGF certification AND the talent is not certified yet,
    // launch the assessment IMMEDIATELY instead of submitting an
    // application first + showing a generic success toast. The
    // application still gets recorded once they pass the assessment.
    if (selectedShift.requires_ggf_certification && !ggfStatus?.ggf_certified) {
      toast.info('This gig requires the Guardian Garage Floors assessment. Launching now…', { duration: 3500 });
      setSelectedShift(null);
      navigate(`/talent/certifications/ggf/assessment?return_to=/talent/gigs/${selectedShift.id}`);
      return;
    }

    setApplying(true);
    try {
      const res = await talentAPI.applyToShift({ shift_id: selectedShift.id, ...(ack ? { payout_terms_acknowledged: true } : {}), ...(cocChecked && cocVersion ? { coc_agreed: true, coc_version_id: cocVersion.id } : {}) });
      const data = res?.data || res || {};
      // Iter-314 — Guardian Garage: application accepted with
      // `pending_certification=true` means the talent still owes the
      // GGF assessment before they can be booked. Skip the generic
      // "someone will be in touch" toast and route them STRAIGHT to
      // the assessment — that's exactly what they need to do next.
      if (data.pending_certification && data.pending_certification_type === 'ggf') {
        toast.success('Application received — complete your assessment to be booked', { duration: 4000 });
        setSelectedShift(null);
        navigate('/talent/certifications/ggf/learn');
        return;
      }
      toast.success(
        data.series_applied_count > 0
          ? `Application submitted — covers all ${data.series_applied_count + 1} shifts of this assignment.`
          : 'Application submitted. Track your status under Applied Gigs.',
        { duration: 5000 },
      );
      // Optimistically flip this card's chip to "Applied" so the user sees
      // immediate feedback before the refreshed list arrives.
      setEligibility((prev) => ({
        ...prev,
        [selectedShift.id]: { status: 'already_applied', missing_count: 0, reason: null },
      }));
      setSelectedShift(null);
      fetchShifts(); // Refresh list
    } catch (error) {
      // Iter-213+ P0 — Compliance gate 403 → redirect to IDV instead of
      // a confusing "Failed to apply" toast.
      const status = error?.response?.status;
      const data = error?.response?.data?.detail;
      if (status === 403 && data && typeof data === 'object' && data.code === 'compliance_gate') {
        setComplianceGate(data);
        setSelectedShift(null);
        toast.error(data.detail || 'Complete IDV to apply for gigs');
        return;
      }
      // Iter-246 / 246d — GGF gate 400 → friendly CTA pushing to the
      // Learning Center first (where the talent can also reach the
      // assessment via the bottom CTA).
      if (status === 400 && data && typeof data === 'object' && data.code === 'ggf_certification_required') {
        toast.error(data.message || 'Guardian Garage Floors Certification Required.', {
          duration: 10000,
          action: {
            label: 'View Training',
            onClick: () => navigate(data.learning_url || data.assessment_url || '/talent/certifications/ggf/learn'),
          },
        });
        return;
      }
      // 2026-08-31 — backend agreement enforcement: send them to sign.
      if (status === 409 && data && typeof data === 'object' && data.code === 'agreement_not_signed') {
        toast.error(data.message || 'Please sign the contractor agreement first.');
        navigate('/talent/agreement');
        return;
      }
      // V424 — payout terms acknowledgment gate. Show the terms and let
      // the talent acknowledge, then retry the apply with the flag set.
      if (status === 428 && data && typeof data === 'object' && data.code === 'payout_terms_ack_required') {
        setTermsChecked(false);
        setCocChecked(false);
        setTermsGate({ shiftId: selectedShift?.id, terms: data.payout_terms });
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const handleFilterChange = () => {
    fetchShifts();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="talent-shifts-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/talent/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-6 text-gray-900">Available Gigs</h1>

        {/* Agreement Required Banner */}
        {agreementStatus && !agreementStatus.has_accepted && (
          <Alert className="mb-6 bg-amber-50 border-amber-300">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Action Required:</strong> You must accept the Independent Contractor Agreement before applying to gigs.{' '}
              <Button 
                variant="link" 
                className="text-amber-700 underline p-0 h-auto font-semibold"
                onClick={() => navigate('/talent/agreement')}
              >
                Review and Accept Agreement
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs for Recommended vs All vs Direct Hire */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full max-w-2xl grid-cols-3">
            <TabsTrigger value="recommended" className="flex items-center gap-2" data-testid="recommended-tab">
              <Sparkles className="w-4 h-4" />
              Recommended
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="all-gigs-tab">
              All iEvntür Gigs
            </TabsTrigger>
            <TabsTrigger value="direct-hire" className="flex items-center gap-2 text-amber-700" data-testid="direct-hire-tab">
              <Building2 className="w-4 h-4" />
              Direct Booking
            </TabsTrigger>
          </TabsList>

          {/* Recommended Gigs Tab */}
          <TabsContent value="recommended" className="mt-4">
            {loadingRecommended ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
              </div>
            ) : recommendedGigs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Sparkles className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-muted-foreground">No recommended gigs at the moment</p>
                  <p className="text-sm text-muted-foreground mt-2">Complete your profile to get better matches</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setActiveTab('all')}
                  >
                    Browse All Gigs
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-500" />
                  Showing {recommendedGigs.length} gigs matched to your profile
                </p>
                <div className="grid gap-4" data-testid="recommended-gigs-list">
                  {recommendedGigs.map((gig) => (
                    <Card
                      key={gig.shift_id}
                      className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-cyan-500"
                      onClick={() => setSelectedShift({
                        id: gig.shift_id,
                        client_id: gig.client_id,
                        role_name: gig.role_name,
                        venue_name: gig.venue_name,
                        company_name: gig.company_name,
                        client_logo_url: gig.client_logo,
                        city: gig.city,
                        state: gig.state,
                        shift_date: gig.shift_date,
                        start_time: gig.start_time,
                        end_time: gig.end_time,
                        talent_pay_rate: gig.hourly_rate,
                        spots_remaining: gig.spots_remaining,
                        match_score: gig.match_score,
                        match_breakdown: gig.match_breakdown,
                        has_active_incentive: gig.has_active_incentive,
                        incentive_display_text: gig.incentive_display_text
                      })}
                      data-testid="recommended-gig-card"
                    >
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h3 className="text-lg font-semibold">{gig.role_name}</h3>
                              {gig.is_invited && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border border-violet-400 bg-violet-50 text-violet-800"
                                  data-testid={`talent-shift-invited-${gig.id}`}
                                  title="You were invited to this shift"
                                >
                                  <Sparkles className="w-3 h-3" />
                                  Invited
                                </span>
                              )}
                              <RoleMatchBadge matchBreakdown={gig.match_breakdown} />
                              <GigMatchBadge score={gig.match_score} breakdown={gig.match_breakdown} />
                              {gig.match_score >= 85 && (
                                <AIPickBadge testId={`talent-shift-ai-pick-${gig.id}`} />
                              )}
                              <BonusBadge shift={gig} />
                              {/* Iter-225r — Talent → Client reputation pill so talent
                                  can vet the client before clicking into the gig. */}
                              <ClientReputationBadge clientId={gig.client_id} variant="compact" />
                            </div>

                            <div className="space-y-1 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                <span>{gig.venue_name || 'Venue'} • {gig.city}, {gig.state}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span>{formatDateShort(gig.shift_date)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                <span>{formatTimeRange(gig.start_time, gig.end_time)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                {gig.is_backup_only ? (
                                  <span className="text-purple-700 font-medium">
                                    {gig.backup_spots_remaining} backup spot{gig.backup_spots_remaining === 1 ? '' : 's'} open
                                  </span>
                                ) : (
                                  <span>{gig.spots_remaining} spots available</span>
                                )}
                              </div>
                              {gig.is_backup_only && (
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-purple-100 text-purple-800 border-purple-200" data-testid="backup-opportunity-badge">
                                    Backup Opportunity
                                  </Badge>
                                </div>
                              )}
                              {gig.company_name && (
                                <div className="flex items-center gap-2 pt-1">
                                  {gig.client_logo ? (
                                    <img src={gig.client_logo} alt={gig.company_name} className="w-4 h-4 rounded object-contain" />
                                  ) : null}
                                  <span className="text-cyan-600 font-medium">{gig.company_name}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="text-right">
                          <div className="text-lg font-bold text-cyan-600">{isFlatPay(gig) ? formatPay(gig) : `$${gig.hourly_rate}/hr`}</div>
                            <div className="text-xs text-gray-500">{isFlatPay(gig) ? 'Flat Rate' : 'Hourly Rate'}</div>
                            {!isFlatPay(gig) && (
                            <div className="text-sm font-semibold text-emerald-600">
                              Net: ${(parseFloat(gig.hourly_rate || 0) - 0.38).toFixed(2)}/hr
                            </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* All Gigs Tab */}
          <TabsContent value="all" className="mt-4">
            {/* Filters */}
            <Card className="mb-6 bg-white border-gray-200">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="shift-filters">
                  <div>
                    <Input
                      placeholder="City"
                      value={filters.city}
                      onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                      data-testid="filter-city"
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="State (e.g., CA)"
                      value={filters.state}
                      onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                      maxLength={2}
                      data-testid="filter-state"
                    />
                  </div>
                  <div>
                    <Select value={filters.role_id || "all"} onValueChange={(value) => setFilters({ ...filters, role_id: value === "all" ? "" : value })}>
                      <SelectTrigger data-testid="filter-role">
                        <SelectValue placeholder="All Roles" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        {roles.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Button onClick={handleFilterChange} className="w-full bg-cyan-500 hover:bg-cyan-600" data-testid="apply-filters">
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Iter-213+ P0 — Compliance gate banner. When IDV is not
                approved, the backend returns 403 with structured data and
                we render the upload-DL CTA inline instead of an empty list. */}
            {complianceGate && !loading && (
              <Card className="border-amber-300 bg-amber-50" data-testid="compliance-gate-banner">
                <CardContent className="p-6 sm:p-8 text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 border border-amber-200 mb-4">
                    <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7 text-amber-700">
                      <path d="M12 2l9 4v6c0 5-3.8 9.5-9 10-5.2-.5-9-5-9-10V6l9-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-amber-900 mb-2">
                    {complianceGate.reason === 'idv_rejected'
                      ? 'Your driver\'s license needs to be re-uploaded'
                      : complianceGate.reason === 'idv_pending_review'
                      ? 'Your driver\'s license is being reviewed'
                      : 'One more step to unlock gigs'}
                  </h3>
                  <p className="text-sm text-amber-800 mb-5 max-w-md mx-auto">
                    {complianceGate.detail || 'Upload your driver\'s license to unlock gig browsing.'}
                  </p>
                  <Button
                    onClick={() => navigate(complianceGate.action?.path || '/talent/idv')}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="compliance-gate-cta"
                  >
                    {complianceGate.action?.label || 'Upload Driver License'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Shifts List */}
            {complianceGate ? null : loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
              </div>
            ) : shifts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">No available shifts found</p>
                  <p className="text-sm text-muted-foreground mt-2">Try adjusting your filters or check back later</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4" data-testid="shifts-list">
                {shifts.map((shift) => (
                  <Card
                    key={shift.id}
                    className={`hover:shadow-md transition-shadow cursor-pointer ${
                      shift.is_featured ? 'ring-2 ring-cyan-200 border-cyan-200' : ''
                    }`}
                    onClick={() => handleSelectShift(shift)}
                    data-testid="shift-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold">{shift.role_name}</h3>
                            {shift.is_invited && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border border-violet-400 bg-violet-50 text-violet-800"
                                data-testid={`talent-shift-invited-${shift.id}`}
                                title="You were invited to this shift"
                              >
                                <Sparkles className="w-3 h-3" />
                                Invited
                              </span>
                            )}
                            {shift.is_featured && (
                              <Badge className="bg-gradient-to-r from-cyan-500 to-pink-500 text-white border-0 shadow-sm text-[10px]" data-testid="boosted-badge">
                                <Sparkles className="w-3 h-3 mr-0.5" />Boosted
                              </Badge>
                            )}
                            {shift.tier_required === 'premium' && (
                              <Badge className="bg-purple-100 text-purple-800">Premium Only</Badge>
                            )}
                            {shift.spots_remaining <= 2 && shift.spots_remaining > 0 && (
                              <Badge className="bg-amber-100 text-amber-800">Limited Spots</Badge>
                            )}
                            {/* Iter-189/190 — Backup / Standby opportunity badges. */}
                            {shift.is_backup_only && (
                              <Badge
                                className="bg-purple-100 text-purple-800 border-purple-200"
                                data-testid="backup-opportunity-badge"
                              >
                                Backup Opportunity
                              </Badge>
                            )}
                            {shift.is_standby_only && (
                              <Badge
                                className="bg-amber-100 text-amber-800 border-amber-300"
                                data-testid="standby-opportunity-badge"
                              >
                                Onsite Standby
                              </Badge>
                            )}
                            <BonusBadge shift={shift} />
                            {/* Multi-Day Shift Badge */}
                            {(shift.duration_days > 1 || (shift.end_date && shift.end_date !== shift.shift_date)) && (
                              <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                                Multi-Day Shift
                              </Badge>
                            )}
                            {/* Certification Required Badge */}
                            {shift.certification_required && (
                              <Badge className="bg-purple-100 text-purple-800 border-purple-200" data-testid="cert-required-badge">
                                <Shield className="w-3 h-3 mr-1" />
                                Cert Required
                              </Badge>
                            )}
                            {/* Smart Apply eligibility chip (fetched in bulk on shifts load) */}
                            <EligibilityChip verdict={eligibility[shift.id]} />
                          </div>

                          <div className="space-y-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2 flex-wrap">
                              <MapPin className="w-4 h-4" />
                              <span>{shift.venue_name || 'Venue'} • {shift.city}, {shift.state}</span>
                              {shift.market_match === 'primary' && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[10px] font-semibold px-2 py-0.5" data-testid="gig-in-your-market">
                                  In your market
                                </span>
                              )}
                              {shift.market_match === 'secondary' && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 text-teal-800 border border-teal-200 text-[10px] font-medium px-2 py-0.5" data-testid="gig-secondary-market">
                                  Your secondary market
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDateShort(shift.gig_date || shift.shift_date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>{formatTimeRange(shift.start_time, shift.end_time)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              {shift.is_backup_only ? (
                                <span className="text-purple-700 font-medium">
                                  {shift.backup_spots_remaining} backup spot{shift.backup_spots_remaining === 1 ? '' : 's'} open
                                </span>
                              ) : (
                                <span>{shift.spots_remaining} spots available</span>
                              )}
                            </div>
                            {shift.company_name && (
                              <div className="flex items-center gap-2 pt-1">
                                {shift.client_logo_url ? (
                                  <img src={shift.client_logo_url} alt={shift.company_name} className="w-4 h-4 rounded object-contain" />
                                ) : null}
                                <span className="text-cyan-600 font-medium">{shift.company_name}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="flex flex-col items-end gap-1">
                            <div className="text-lg font-bold text-cyan-600">{isFlatPay(shift) ? formatPay(shift) : `$${shift.hourly_rate || shift.talent_pay_rate}/hr`}</div>
                            <div className="text-xs text-gray-500">{isFlatPay(shift) ? 'Flat Rate' : 'Hourly Rate'}</div>
                            {!isFlatPay(shift) && (
                            <div className="text-sm font-semibold text-emerald-600">
                              Net: ${(parseFloat(shift.hourly_rate || shift.talent_pay_rate || 0) - 0.38).toFixed(2)}/hr
                            </div>
                            )}
                            {shift.has_active_incentive && shift.incentive_display_text && (
                              <div className="text-xs font-bold text-orange-500" data-testid="bonus-amount-display">
                                {shift.incentive_display_text}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Direct Hire Jobs Tab */}
          <TabsContent value="direct-hire" className="mt-4">
            {/* Direct Hire Notice */}
            <Alert className="mb-6 bg-amber-50 border-amber-300">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Direct Booking Jobs</strong> - These jobs are posted by agencies and companies booking talent directly.
                Payment is made by the hiring party, <strong>not through the iEvntür platform</strong>. 
                iEvntür does not process payouts or provide dispute resolution for direct booking positions.
              </AlertDescription>
            </Alert>

            {/* Filters for Direct Hire */}
            <Card className="mb-6 bg-white border-gray-200">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input
                    placeholder="City"
                    value={filters.city}
                    onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  />
                  <Input
                    placeholder="State (e.g., CA)"
                    value={filters.state}
                    onChange={(e) => setFilters({ ...filters, state: e.target.value.toUpperCase() })}
                    maxLength={2}
                  />
                  <Select value={filters.role_id || "all"} onValueChange={(value) => setFilters({ ...filters, role_id: value === "all" ? "" : value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {roles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={fetchAgencyJobs} className="bg-amber-500 hover:bg-amber-600">
                    Search
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Agency Jobs List */}
            {loadingAgency ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : agencyJobs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">No direct booking jobs available</p>
                  <p className="text-sm text-muted-foreground mt-2">Check back later or try different filters</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4" data-testid="agency-jobs-list">
                {agencyJobs.map((job) => (
                  <Card
                    key={job.id}
                    className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-amber-400 bg-amber-50/30"
                    onClick={() => { setCocChecked(false); setSelectedAgencyJob(job); }}
                    data-testid="agency-job-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold">{job.title}</h3>
                            <Badge className="bg-amber-500 text-white border-0 font-bold text-[10px] tracking-wider" data-testid="direct-hire-badge">
                              DIRECT BOOKING
                            </Badge>
                            {job.spots_remaining <= 2 && job.spots_remaining > 0 && (
                              <Badge className="bg-red-100 text-red-800">Only {job.spots_remaining} spots</Badge>
                            )}
                            {job.is_backup_only && (
                              <Badge className="bg-purple-100 text-purple-800 border-purple-200" data-testid="backup-opportunity-badge">
                                Backup Opportunity
                              </Badge>
                            )}
                          </div>

                          <div className="space-y-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              <span>{job.company_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{job.venue_name} • {job.city}, {job.state}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDateShort(job.job_date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>{formatTimeRange(job.start_time, job.end_time)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              <span>{job.spots_remaining} spots available</span>
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-2xl font-bold text-amber-600">${job.hourly_rate}/hr</div>
                          <div className="text-sm text-muted-foreground">Pay Rate</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Agency Job Details Dialog */}
      <Dialog open={!!selectedAgencyJob} onOpenChange={() => setSelectedAgencyJob(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedAgencyJob && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selectedAgencyJob.title}</DialogTitle>
                <DialogDescription>
                  {selectedAgencyJob.company_name} • {selectedAgencyJob.city}, {selectedAgencyJob.state}
                </DialogDescription>
              </DialogHeader>

              {/* Direct Hire Warning */}
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  <strong>Direct Booking Notice:</strong> This is a direct booking opportunity. Payment will be made directly by the booking party, not through the iEvntür platform.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Date</div>
                    <div className="font-medium">
                      {formatDate(selectedAgencyJob.job_date)}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Time</div>
                    <div className="font-medium">{formatTimeRange(selectedAgencyJob.start_time, selectedAgencyJob.end_time)}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Pay Rate</div>
                    <div className="font-medium text-amber-600 text-xl">${selectedAgencyJob.hourly_rate}/hr</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Spots Available</div>
                    <div className="font-medium">{selectedAgencyJob.spots_remaining}</div>
                  </div>
                </div>

                {selectedAgencyJob.description && (
                  <div>
                    <h4 className="font-medium mb-2">Job Description</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedAgencyJob.description}</p>
                  </div>
                )}

                {selectedAgencyJob.responsibilities && (
                  <div>
                    <h4 className="font-medium mb-2">Responsibilities</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedAgencyJob.responsibilities}</p>
                  </div>
                )}

                {selectedAgencyJob.dress_code && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="font-medium mb-1 text-blue-800">Dress Code / Appearance</h4>
                    <p className="text-sm text-blue-700">{selectedAgencyJob.dress_code}</p>
                  </div>
                )}

                {selectedAgencyJob.arrival_instructions && (
                  <div>
                    <h4 className="font-medium mb-2">Arrival Instructions</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedAgencyJob.arrival_instructions}</p>
                  </div>
                )}

                {selectedAgencyJob.requirements && (
                  <div>
                    <h4 className="font-medium mb-2">Requirements</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedAgencyJob.requirements}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium">Cover Note (Optional)</label>
                  <Textarea
                    value={coverNote}
                    onChange={(e) => setCoverNote(e.target.value)}
                    placeholder="Introduce yourself and share why you're a great fit..."
                    rows={3}
                    className="mt-1"
                    data-testid="cover-note-input"
                  />
                </div>
                {cocVersion && (
                  <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 space-y-2" data-testid="agency-apply-coc-section">
                    <button type="button" onClick={() => setShowCocView(true)} className="text-xs font-semibold text-teal-800 underline" data-testid="agency-apply-coc-review-btn">
                      Review the iEvntür Gig Code of Conduct
                    </button>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={cocChecked} onChange={(e) => setCocChecked(e.target.checked)} className="mt-0.5 h-4 w-4 accent-teal-600" data-testid="agency-apply-coc-ack-checkbox" />
                      <span className="text-xs text-gray-700">{cocVersion.checkbox_text}</span>
                    </label>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedAgencyJob(null)}>Cancel</Button>
                <Button 
                  onClick={handleApplyToAgencyJob}
                  disabled={applying || (cocVersion && !cocChecked)}
                  className="bg-amber-500 hover:bg-amber-600"
                  data-testid="apply-agency-btn"
                >
                  {applying ? 'Applying...' : 'Apply (Direct Booking)'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Direct Hire Confirmation */}
      <Dialog open={showDirectHireConfirm} onOpenChange={setShowDirectHireConfirm}>
        <DialogContent>
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <DialogTitle className="text-xl mb-2">Application Submitted!</DialogTitle>
            <DialogDescription className="mb-4">
              Your application has been sent to the hiring party. If accepted, they will contact you directly for next steps.
            </DialogDescription>
            <Alert className="bg-amber-50 border-amber-200 text-left">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-700 text-sm">
                Remember: This is a direct booking job. If accepted, payment will be made directly by the booking party, not through iEvntür.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowDirectHireConfirm(false)} className="w-full">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Details Dialog */}
      <Dialog open={!!selectedShift} onOpenChange={() => setSelectedShift(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedShift && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <DialogTitle className="text-2xl">{selectedShift.role_name}</DialogTitle>
                  {selectedShift.match_score && (
                    <GigMatchBadge score={selectedShift.match_score} breakdown={selectedShift.match_breakdown} />
                  )}
                </div>
                <DialogDescription>
                  {selectedShift.venue_name} • {selectedShift.city}, {selectedShift.state}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                {/* Iter-225r — Talent → Client reputation panel, ABOVE
                    everything else so talent see the trust signal before
                    they apply / accept. */}
                {selectedShift.client_id && (
                  <ClientReputationBadge clientId={selectedShift.client_id} variant="detail" />
                )}

                {/* Multi-Day Assignment Notice — driven by the real schedule */}
                {(gigSchedule?.is_multi_day || selectedShift.duration_days > 1 || (selectedShift.end_date && selectedShift.end_date !== selectedShift.shift_date)) && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <Calendar className="w-4 h-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <strong>Multi-day assignment:</strong> Talent is expected to complete all scheduled shifts.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Multi-Location Notice (when applicable) */}
                {selectedShift.is_multi_location && (
                  <Alert className="bg-amber-50 border-amber-200">
                    <MapPin className="w-4 h-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <strong>Multi-location assignment:</strong> You may be assigned to one or more locations based on availability. Selection for one shift does not guarantee assignment across all locations.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Match Score Highlight */}
                {selectedShift.match_score && (
                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4 border border-cyan-200">
                    <div className="flex items-center gap-2 text-cyan-700 font-medium mb-1">
                      <Zap className="w-4 h-4" />
                      Why this shift matches you
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      {selectedShift.match_breakdown?.role?.score >= 80 && <div>Matches your primary role</div>}
                      {selectedShift.match_breakdown?.distance?.score >= 80 && <div>Located in your preferred area</div>}
                      {selectedShift.match_breakdown?.pay_rate?.score >= 70 && <div>Pay rate meets your expectations</div>}
                      {selectedShift.match_breakdown?.availability?.score >= 80 && <div>Fits your availability</div>}
                      {selectedShift.match_breakdown?.experience?.score >= 60 && <div>Good fit for your experience level</div>}
                    </div>
                  </div>
                )}

                {/* 1. Location + Map */}
                <div className="bg-slate-50 rounded-lg p-4" data-testid="shift-location-section">
                  <h4 className="font-semibold mb-2 text-slate-900 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-cyan-600" />
                    Location
                  </h4>
                  <div className="font-medium">{selectedShift.venue_name}</div>
                  {isLocked(selectedShift.venue_address) ? (
                    <div className="text-xs text-gray-500 italic flex items-center gap-1 mt-0.5">
                      <Lock className="w-3 h-3" /> Full address unlocks after booking
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">{selectedShift.venue_address}</div>
                  )}
                  <div className="text-sm text-gray-500">{selectedShift.city}, {selectedShift.state}</div>
                  
                  {/* Google Maps Preview — only shown when address is unlocked */}
                  {!isLocked(selectedShift.venue_address) && selectedShift.venue_lat && selectedShift.venue_lng && (
                    <div className="mt-3 rounded-lg overflow-hidden border border-gray-200">
                      <iframe
                        title="Shift Location Map"
                        width="100%"
                        height="150"
                        frameBorder="0"
                        style={{ border: 0 }}
                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${selectedShift.venue_lat},${selectedShift.venue_lng}&zoom=14`}
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Get Directions CTA — opens Apple/Google Maps app with routing */}
                  {(selectedShift.venue_lat && selectedShift.venue_lng) || selectedShift.venue_address ? (
                    <button
                      type="button"
                      data-testid="shift-get-directions-btn"
                      onClick={() => {
                        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                        let url;
                        if (selectedShift.venue_lat && selectedShift.venue_lng) {
                          url = isIOS
                            ? `https://maps.apple.com/?daddr=${selectedShift.venue_lat},${selectedShift.venue_lng}`
                            : `https://www.google.com/maps/dir/?api=1&destination=${selectedShift.venue_lat},${selectedShift.venue_lng}`;
                        } else {
                          const q = encodeURIComponent([selectedShift.venue_address, selectedShift.city, selectedShift.state].filter(Boolean).join(', '));
                          url = `https://www.google.com/maps/dir/?api=1&destination=${q}`;
                        }
                        window.open(url, '_blank', 'noopener');
                      }}
                      className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 transition"
                    >
                      <MapPin className="w-4 h-4" />
                      Get Directions
                    </button>
                  ) : null}
                  
                  {/* Distance Display — Iter-196: defensive Number cast,
                      backend may serialize this as a string when it
                      comes from a Decimal column. */}
                  {selectedShift.distance_miles != null && Number.isFinite(Number(selectedShift.distance_miles)) && (
                    <div className="mt-2 flex items-center gap-1 text-sm text-cyan-600">
                      <MapPin className="w-3 h-3" />
                      <span>{Number(selectedShift.distance_miles).toFixed(1)} miles from your location</span>
                    </div>
                  )}
                </div>

                {/* 2. Schedule */}
                <div className="grid grid-cols-2 gap-4" data-testid="shift-schedule-section">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Date</div>
                    <div className="font-medium">
                      {formatDateFull(selectedShift.gig_date || selectedShift.shift_date)}
                    </div>
                    {selectedShift.end_date && selectedShift.end_date !== selectedShift.shift_date && (
                      <div className="text-sm text-cyan-600 font-medium mt-1">
                        {formatDateRange(selectedShift.shift_date, selectedShift.end_date)} ({selectedShift.duration_days || Math.ceil((new Date(selectedShift.end_date) - new Date(selectedShift.shift_date)) / (1000 * 60 * 60 * 24)) + 1}-day shift)
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Shift Time</div>
                    <div className="font-medium">{formatTimeRange(selectedShift.start_time, selectedShift.end_time)}</div>
                    <div className="text-sm text-gray-500 mt-1">{selectedShift.spots_remaining} spots available</div>
                  </div>
                </div>

                {/* 2b. Full Assignment Schedule — every shift in a multi-day
                    assignment, with totals computed from these rows. */}
                {gigSchedule?.is_multi_day && (
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-testid="full-assignment-schedule">
                    <h4 className="font-semibold mb-2 text-slate-900 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-cyan-600" />
                      Full Assignment Schedule ({gigSchedule.shifts.length} shifts)
                    </h4>
                    <div className="space-y-1.5">
                      {gigSchedule.shifts.map((s, i) => (
                        <div key={`${s.shift_id}-${s.date}`} className="flex items-center justify-between text-sm" data-testid={`schedule-shift-${i + 1}`}>
                          <span className="text-gray-700">
                            <span className="font-medium">Shift {i + 1}:</span>{' '}
                            {formatDateFull(s.date)}, {formatTimeRange(s.start_time, s.end_time)}
                          </span>
                          <span className="font-medium text-gray-900">{s.hours} hrs</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between text-sm pt-2 mt-1 border-t border-slate-300">
                        <span className="font-semibold text-gray-900">Total scheduled hours</span>
                        <span className="font-bold text-cyan-700" data-testid="schedule-total-hours">{gigSchedule.total_hours} hrs</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 3. Pay Breakdown - Clear & Transparent */}
                <div className="bg-gradient-to-br from-cyan-50 to-emerald-50 rounded-lg p-4 border border-cyan-200" data-testid="shift-pay-breakdown">
                  <h4 className="font-semibold mb-3 text-slate-900 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-cyan-600" />
                    Pay Breakdown
                  </h4>
                  {(() => {
                    const hourlyRate = parseFloat(selectedShift.hourly_rate || selectedShift.talent_pay_rate || 0);
                    const oaDeduction = 0.38; // Standard OA Insurance deduction
                    const netRate = Math.max(0, hourlyRate - oaDeduction);
                    
                    // Calculate shift duration in hours
                    const startParts = selectedShift.start_time?.split(':') || [0, 0];
                    const endParts = selectedShift.end_time?.split(':') || [0, 0];
                    const startHours = parseInt(startParts[0]) + parseInt(startParts[1]) / 60;
                    const endHours = parseInt(endParts[0]) + parseInt(endParts[1]) / 60;
                    const shiftHours = endHours > startHours ? endHours - startHours : (24 - startHours + endHours);

                    // 2026-08-31 — totals come from the full assignment
                    // schedule (every displayed shift) when available, so
                    // multi-day earnings always match the listed shifts.
                    const totalHours = gigSchedule ? gigSchedule.total_hours : shiftHours;
                    const estimatedTotal = gigSchedule
                      ? gigSchedule.estimated_total_earnings.toFixed(2)
                      : (netRate * shiftHours).toFixed(2);
                    const shiftCount = gigSchedule?.shifts?.length || 1;
                    
                    return (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700">Hourly Rate</span>
                          <span className="font-bold text-lg text-gray-900">${hourlyRate.toFixed(2)}/hr</span>
                        </div>
                        <div className="flex justify-between items-center text-red-600">
                          <span className="flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            OA Insurance
                          </span>
                          <span className="font-medium">-${oaDeduction.toFixed(2)}/hr</span>
                        </div>
                        <div className="border-t border-cyan-200 pt-2 flex justify-between items-center">
                          <span className="text-gray-900 font-semibold">Net Rate</span>
                          <span className="font-bold text-xl text-cyan-600">${netRate.toFixed(2)}/hr</span>
                        </div>
                        <div className="border-t border-cyan-200 pt-3 mt-2">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">Estimated Total Earnings</span>
                            <span className="font-bold text-emerald-600" data-testid="estimated-total-earnings">${estimatedTotal}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1" data-testid="earnings-hours-note">
                            Based on {Number(totalHours).toFixed(1)} total scheduled hours
                            {shiftCount > 1 ? ` across ${shiftCount} shifts` : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* 5. Duties & Responsibilities — Iter-367: always visible
                    pre-booking so talent can decide before accepting. */}
                <div data-testid="gig-duties">
                  <h4 className="font-semibold mb-2 text-slate-900">Duties &amp; Responsibilities</h4>
                  {selectedShift.duties && !isLocked(selectedShift.duties) ? (
                    <p className="text-sm text-gray-600 whitespace-pre-wrap" data-testid="gig-duties-body">
                      {selectedShift.duties}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 italic" data-testid="gig-duties-empty">
                      No additional duties have been provided yet. Contact Operations before confirming if you need clarification.
                    </p>
                  )}
                </div>

                {/* 6. Dress Code — Iter-367: unlocked pre-booking; render the
                    actual value entered by the admin. Falls back to a
                    contact-Ops message when nothing is set. */}
                {(() => {
                  const hasName = selectedShift.dress_code_name && !isLocked(selectedShift.dress_code_name);
                  const hasBody = selectedShift.dress_code && !isLocked(selectedShift.dress_code);
                  const hasChecklist = Array.isArray(selectedShift.dress_code_checklist) && selectedShift.dress_code_checklist.length > 0;
                  const hasDescription = selectedShift.dress_code_description && !isLocked(selectedShift.dress_code_description);
                  const hasImages = Array.isArray(selectedShift.dress_code_images) && selectedShift.dress_code_images.length > 0;
                  const hasAny = hasName || hasBody || hasChecklist || hasDescription || hasImages;
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="gig-dress-code">
                      <h4 className="font-semibold mb-2 text-blue-900 flex items-center gap-2">
                        Dress Code{hasName ? `: ${selectedShift.dress_code_name}` : ''}
                      </h4>
                      {hasBody && (
                        <p className="text-sm text-blue-800 whitespace-pre-wrap mb-2" data-testid="gig-dress-code-body">{selectedShift.dress_code}</p>
                      )}
                      {hasChecklist && (
                        <ul className="space-y-1 mb-2" data-testid="gig-dress-code-checklist">
                          {selectedShift.dress_code_checklist.map((item, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-blue-800">
                              <CheckCircle className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                      {hasDescription && (
                        <p className="text-sm text-blue-700">{selectedShift.dress_code_description}</p>
                      )}
                      {hasImages && (
                        <div className="flex gap-2 mt-3" data-testid="gig-dress-code-images">
                          {selectedShift.dress_code_images.map((url, i) => (
                            <img key={i} src={url} alt="Dress code reference" className="w-24 h-24 rounded-lg object-cover border border-blue-200" />
                          ))}
                        </div>
                      )}
                      {!hasAny && (
                        <p className="text-sm text-blue-700 italic" data-testid="gig-dress-code-empty">
                          No dress code has been provided yet. Contact Operations before confirming if you need clarification.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* 6a. Confirmation & Cancellation Policy */}
                {(selectedShift.confirmation_required_hours || selectedShift.cancellation_cutoff_hours) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="gig-policy">
                    <h4 className="font-semibold mb-2 text-amber-900">Confirmation &amp; Cancellation Policy</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-amber-900">
                      <div>
                        <span className="font-medium">Confirm within:</span>{' '}
                        {selectedShift.confirmation_required_hours ?? 24}h of booking
                      </div>
                      <div>
                        <span className="font-medium">Cancel cutoff:</span>{' '}
                        {selectedShift.cancellation_cutoff_hours ?? 24}h before shift start
                      </div>
                    </div>
                    <p className="text-xs text-amber-700 mt-2">Late cancels and no-shows affect your reliability score.</p>
                  </div>
                )}

                {/* 6b. Important Notes — locked for non-booked viewers */}
                {selectedShift.important_notes && (
                  isLocked(selectedShift.important_notes) ? (
                    <LockedBanner label="Important Notes" />
                  ) : (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="shift-important-notes">
                      <div className="font-medium text-blue-900 mb-1">Important Notes</div>
                      <div className="text-sm text-blue-900">
                        <LinkifiedText text={selectedShift.important_notes} />
                      </div>
                    </div>
                  )
                )}

                {/* 6b. Attire Restrictions */}
                {selectedShift.attire_restrictions?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="gig-attire-restrictions">
                    <h4 className="font-semibold mb-2 text-red-900">Attire Restrictions</h4>
                    <div className="grid grid-cols-2 gap-1">
                      {selectedShift.attire_restrictions.map((r, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-red-700">
                          <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                          {r}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 7. Grooming Requirements */}
                {selectedShift.grooming_requirements?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="gig-grooming">
                    <h4 className="font-semibold mb-2 text-amber-900">Grooming Requirements</h4>
                    <div className="grid grid-cols-1 gap-1">
                      {selectedShift.grooming_requirements.map((r, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-amber-800">
                          <CheckCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          {r}
                        </div>
                      ))}
                    </div>
                    {selectedShift.grooming_images?.length > 0 && (
                      <div className="flex gap-2 mt-3">
                        {selectedShift.grooming_images.map((url, i) => (
                          <img key={i} src={url} alt="Grooming reference" className="w-24 h-24 rounded-lg object-cover border border-amber-200" />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Send Home Warning */}
                {selectedShift.send_home_warning && (
                  <div className="bg-red-100 border border-red-300 rounded-lg p-3 flex items-center gap-2" data-testid="send-home-warning">
                    <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                    <p className="text-sm font-medium text-red-800">This partner may send you home if these requirements are not met.</p>
                  </div>
                )}

                {/* 8. Additional Notes — locked for non-booked viewers */}
                {selectedShift.additional_notes && (
                  isLocked(selectedShift.additional_notes) ? (
                    <LockedBanner label="Additional Notes & Instructions" />
                  ) : (
                    <div data-testid="gig-additional-notes">
                      <h4 className="font-semibold mb-2 text-slate-900">Additional Notes & Instructions</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedShift.additional_notes}</p>
                    </div>
                  )
                )}

                {/* Tier Badge */}
                {selectedShift.tier_required === 'premium' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-800">Premium Only</Badge>
                      <span className="text-sm text-purple-900">This shift requires Premium tier membership</span>
                    </div>
                  </div>
                )}
                
                {/* Iter-313f — GGF certification is now a POST-apply
                    requirement. Show an amber "cert required after apply"
                    banner alongside the enabled Apply Now button. */}
                {selectedShift.requires_ggf_certification && !ggfStatus?.ggf_certified && (
                  <div className="rounded-lg p-4 border bg-amber-50 border-amber-200" data-testid="ggf-cert-required-banner">
                    <h4 className="font-semibold mb-2 flex items-center gap-2 text-amber-900">
                      <Award className="w-5 h-5 text-amber-700" />
                      Certification required after applying
                    </h4>
                    <p className="text-sm text-amber-900/90 mb-3">
                      You can apply now. To be booked for this Guardian Garage
                      Floors shift, complete the GGF assessment (about 15 min).
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        onClick={() => navigate('/talent/certifications/ggf/learn')}
                        variant="outline"
                        className="border-amber-400 text-amber-800 hover:bg-amber-100 flex-1"
                        data-testid="ggf-view-training-btn"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        View GGF Training
                      </Button>
                      <Button
                        onClick={() => navigate('/talent/certifications/ggf')}
                        className="bg-amber-600 hover:bg-amber-700 text-white flex-1"
                        data-testid="ggf-take-assessment-btn"
                      >
                        <Award className="w-4 h-4 mr-2" />
                        Take Assessment
                      </Button>
                    </div>
                  </div>
                )}

                {/* Prerequisites / Training Requirements */}
                {selectedShift.certification_required && (
                  <div className={`rounded-lg p-4 border ${
                    checkingPrereqs 
                      ? 'bg-gray-50 border-gray-200' 
                      : prerequisiteCheck?.can_apply 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                  }`} data-testid="cert-requirements-section">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      {prerequisiteCheck?.can_apply ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Lock className="w-5 h-5 text-red-600" />
                      )}
                      Prerequisites / Training
                    </h4>
                    
                    {checkingPrereqs ? (
                      <div className="flex items-center gap-2 text-gray-600">
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        Checking your certifications...
                      </div>
                    ) : prerequisiteCheck?.can_apply ? (
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">You meet all certification requirements</span>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-red-700">
                          <Lock className="w-5 h-5" />
                          <span className="font-medium">Complete prerequisites to unlock this shift</span>
                        </div>
                        
                        {prerequisiteCheck?.missing_certifications?.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm text-red-600">Missing certifications:</p>
                            <div className="flex flex-wrap gap-2">
                              {prerequisiteCheck.missing_certifications.map(cert => (
                                <Badge key={cert.module_id} variant="destructive" className="bg-red-100 text-red-700 border-red-300">
                                  <Award className="w-3 h-3 mr-1" />
                                  {cert.badge_name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <Button 
                          onClick={() => navigate('/talent/training')}
                          className="bg-purple-600 hover:bg-purple-700 w-full mt-2"
                        >
                          <BookOpen className="w-4 h-4 mr-2" />
                          Go to Training Hub
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Cancellation Policy - Talent Facing */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="cancellation-policy-section">
                  <h4 className="font-semibold mb-3 text-amber-900 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Cancellation Policy
                  </h4>
                  <ul className="space-y-2 text-sm text-amber-800">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">•</span>
                      <span>Late cancellations may impact your future booking eligibility.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">•</span>
                      <span>No-shows may result in account restrictions.</span>
                    </li>
                  </ul>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedShift(null)}>
                  Close
                </Button>
                <OneTapApplyButton
                  shiftId={selectedShift.id}
                  onApplied={() => {
                    // refresh card-level eligibility map after a successful apply
                    setEligibility(prev => ({
                      ...prev,
                      [selectedShift.id]: { status: 'already_applied', missing_count: 0 },
                    }));
                  }}
                />
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* V424 — Payout terms acknowledgment gate */}
      <Dialog open={!!termsGate} onOpenChange={(o) => { if (!o) setTermsGate(null); }}>
        <DialogContent className="bg-white max-w-md" data-testid="payout-terms-ack-dialog">
          <DialogHeader>
            <DialogTitle>Payout terms for this gig</DialogTitle>
            <DialogDescription>Review how and when you'll be paid before you apply.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-sm font-semibold text-emerald-900" data-testid="payout-terms-days">
                Paid within {termsGate?.terms?.business_days ?? 30} business days
              </p>
              <p className="text-xs text-emerald-800 mt-1">{termsGate?.terms?.disclaimer}</p>
              {termsGate?.terms?.expedited && (
                <Badge className="mt-2 bg-amber-100 text-amber-800 border border-amber-300">
                  {termsGate.terms.expedited_label}
                </Badge>
              )}
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-cyan-600"
                data-testid="payout-terms-ack-checkbox"
              />
              <span className="text-xs text-gray-700">
                I have read and acknowledge the payout terms for this gig, including the expected payment timeframe.
              </span>
            </label>
            {cocVersion && (
              <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 space-y-2" data-testid="apply-coc-section">
                <button type="button" onClick={() => setShowCocView(true)} className="text-xs font-semibold text-teal-800 underline" data-testid="apply-coc-review-btn">
                  Review the iEvntür Gig Code of Conduct
                </button>
                <label className="flex items-start gap-2 cursor-pointer" data-testid="apply-coc-ack-label">
                  <input type="checkbox" checked={cocChecked} onChange={(e) => setCocChecked(e.target.checked)} className="mt-0.5 h-4 w-4 accent-teal-600" data-testid="apply-coc-ack-checkbox" />
                  <span className="text-xs text-gray-700">{cocVersion.checkbox_text}</span>
                </label>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTermsGate(null)} data-testid="payout-terms-cancel-btn">Cancel</Button>
            <Button
              disabled={!termsChecked || (cocVersion && !cocChecked) || applying}
              onClick={async () => { setTermsGate(null); await handleApply(true); }}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="payout-terms-confirm-apply-btn"
            >
              Acknowledge & Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <GigCodeOfConductModal
        open={showCocView}
        version={cocVersion}
        readOnly
        heading={cocVersion ? (cocVersion.title || 'Code of Conduct') : undefined}
        onClose={() => setShowCocView(false)}
      />
    </div>
  );
};

export default TalentShifts;